import { makeStyles, tokens } from '@fluentui/react-components';
import type {
  CardStaticFieldKey,
  ICardSlot,
  ICardStaticFieldBinding,
  IField,
  IMetadata,
} from '../../services/metadataService';

interface ICardPreviewEntry {
  key: string;
  label: string;
}

interface ICardPreviewProps {
  metadata?: IMetadata;
  showBorders?: boolean;
  showSlotNames?: boolean;
}

const PREVIEW_CARD_INDICES = [1, 2, 3];

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  cards: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  card: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalM,
    borderRadius: '10px',
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    padding: '14px 16px',
    cursor: 'pointer',
    transitionProperty: 'box-shadow, border-color',
    transitionDuration: '150ms',
  },
  cardBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    flexGrow: 1,
    minWidth: 0,
  },
  blockSlot: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    minWidth: 0,
  },
  inlineSlot: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '6px',
    minWidth: 0,
    flexGrow: 1,
  },
  slotRow: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalS,
  },
  slotContent: {
    flexGrow: 1,
    minWidth: 0,
  },
  slotDivider: {
    borderTop: '1px dotted rgba(0, 0, 0, 0.2)',
    margin: '8px 0',
  },
  inlineField: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    minWidth: 0,
  },
  fieldText: {
    lineHeight: '20px',
    overflowWrap: 'anywhere',
  },
  separator: {
    color: tokens.colorNeutralForeground4,
  },
  slotName: {
    display: 'block',
    fontFamily: 'Consolas, "Courier New", monospace',
    fontSize: '11px',
    color: tokens.colorNeutralForeground3,
    whiteSpace: 'nowrap',
  },
  emptyState: {
    color: tokens.colorNeutralForeground3,
  },
});

const getSortedSlots = (metadata?: IMetadata): ICardSlot[] =>
  [...(metadata?.cardLayout?.slots ?? [])].sort(
    (slotA, slotB) => (slotA.order ?? 0) - (slotB.order ?? 0)
  );

const getSlotFields = (
  metadata: IMetadata | undefined,
  slotId: string
): IField[] =>
  [...(metadata?.fields ?? [])]
    .filter((field) => field.cardSlotId === slotId)
    .sort(
      (fieldA, fieldB) =>
        (fieldA.cardSlotOrder ?? Number.MAX_SAFE_INTEGER) -
          (fieldB.cardSlotOrder ?? Number.MAX_SAFE_INTEGER) ||
        (fieldA.label || fieldA.name).localeCompare(fieldB.label || fieldB.name)
    );

const getStaticFieldLabel = (key: CardStaticFieldKey): string => {
  switch (key) {
    case 'id':
      return 'Id';
    case 'code':
      return 'Code';
    case 'name':
      return 'Name';
    case 'description':
      return 'Description';
    case 'status':
      return 'Status';
    case 'entityTypeName':
      return 'Entity type name';
    default:
      return key;
  }
};

const getStaticFieldBindingsForSlot = (
  metadata: IMetadata | undefined,
  slotId: string,
  position: ICardStaticFieldBinding['position']
): ICardStaticFieldBinding[] =>
  (metadata?.cardLayout?.staticFields ?? []).filter(
    (b) => b.slotId === slotId && b.position === position
  );

const getSlotEntries = (
  metadata: IMetadata | undefined,
  slotId: string
): ICardPreviewEntry[] => {
  const startBindings = getStaticFieldBindingsForSlot(
    metadata,
    slotId,
    'start'
  );
  const endBindings = getStaticFieldBindingsForSlot(metadata, slotId, 'end');
  const fields = getSlotFields(metadata, slotId);

  const startEntries: ICardPreviewEntry[] = startBindings.map((b) => ({
    key: `static-${b.key}`,
    label: getStaticFieldLabel(b.key),
  }));
  const fieldEntries: ICardPreviewEntry[] = fields.map((f) => ({
    key: f.name,
    label: f.label || f.name,
  }));
  const endEntries: ICardPreviewEntry[] = endBindings.map((b) => ({
    key: `static-${b.key}`,
    label: getStaticFieldLabel(b.key),
  }));

  return [...startEntries, ...fieldEntries, ...endEntries];
};

const getDefaultFontSize = (slot: ICardSlot, slotIndex: number) => {
  switch (slot.style?.fontSize) {
    case 'xs':
      return 12;
    case 'sm':
      return 13;
    case 'lg':
      return 20;
    case 'md':
      return 16;
    default:
      return slotIndex === 0 ? 20 : slot.style?.display === 'inline' ? 12 : 14;
  }
};

const getInlineJustifyContent = (align?: string) => {
  switch (align) {
    case 'center':
      return 'center';
    case 'right':
      return 'flex-end';
    case 'left':
    default:
      return 'flex-start';
  }
};

const getPreviewValue = (entry: ICardPreviewEntry, cardIndex: number) =>
  `${entry.label}-value[${cardIndex}]`;

const getFieldPreviewText = (entry: ICardPreviewEntry, cardIndex: number) =>
  getPreviewValue(entry, cardIndex);

const CardPreview = ({
  metadata,
  showBorders = true,
  showSlotNames = true,
}: ICardPreviewProps) => {
  const styles = useStyles();

  const boundSlots = getSortedSlots(metadata)
    .map((slot) => ({
      slot,
      entries: getSlotEntries(metadata, slot.id),
    }))
    .filter((item) => item.entries.length > 0);

  return (
    <div className={styles.container}>
      {metadata?.cardLayout?.slots?.length ? null : (
        <div className={styles.emptyState}>No card slots defined yet.</div>
      )}
      {metadata?.cardLayout?.slots?.length && boundSlots.length === 0 ? (
        <div className={styles.emptyState}>
          No fields are currently bound to card slots.
        </div>
      ) : null}
      {boundSlots.length > 0 ? (
        <div className={styles.cards}>
          {PREVIEW_CARD_INDICES.map((cardIndex) => (
            <div
              key={cardIndex}
              className={styles.card}
              style={
                cardIndex === 2
                  ? { borderColor: '#0078d4', boxShadow: '0 0 0 1px #0078d4' }
                  : undefined
              }
            >
              <div className={styles.cardBody}>
                {boundSlots.map(({ slot, entries }, slotIndex) => {
                  const isInline = slot.style?.display === 'inline';
                  const isLastSlot = slotIndex === boundSlots.length - 1;
                  const fontSize = getDefaultFontSize(slot, slotIndex);
                  const fontWeight =
                    slotIndex === 0 && entries.length === 1 ? 600 : 400;
                  const sharedStyle = {
                    color: slot.style?.color ?? undefined,
                    fontSize: `${fontSize}px`,
                    fontWeight,
                    textAlign: (slot.style?.align ?? 'left') as
                      | 'left'
                      | 'center'
                      | 'right',
                  };

                  if (isInline) {
                    return (
                      <>
                        <div key={slot.id} className={styles.slotRow}>
                          <div className={styles.slotContent}>
                            <div
                              className={styles.inlineSlot}
                              style={{
                                ...sharedStyle,
                                justifyContent: getInlineJustifyContent(
                                  slot.style?.align
                                ),
                              }}
                            >
                              {entries.map((entry, entryIndex) => (
                                <div
                                  key={`${slot.id}-${entry.key}`}
                                  className={styles.inlineField}
                                >
                                  <div className={styles.fieldText}>
                                    {getFieldPreviewText(entry, cardIndex)}
                                  </div>
                                  {entryIndex < entries.length - 1 ? (
                                    <span className={styles.separator}>|</span>
                                  ) : null}
                                </div>
                              ))}
                            </div>
                          </div>
                          <code
                            className={styles.slotName}
                            style={{
                              display: showSlotNames ? 'block' : 'none',
                            }}
                          >
                            {slot.id}
                          </code>
                        </div>
                        {showBorders && !isLastSlot && (
                          <div className={styles.slotDivider} />
                        )}
                      </>
                    );
                  }

                  return (
                    <>
                      <div key={slot.id} className={styles.slotRow}>
                        <div className={styles.slotContent}>
                          <div className={styles.blockSlot} style={sharedStyle}>
                            {entries.map((entry) => (
                              <div
                                key={`${slot.id}-${entry.key}`}
                                className={styles.fieldText}
                              >
                                {getFieldPreviewText(entry, cardIndex)}
                              </div>
                            ))}
                          </div>
                        </div>
                        <code
                          className={styles.slotName}
                          style={{ display: showSlotNames ? 'block' : 'none' }}
                        >
                          {slot.id}
                        </code>
                      </div>
                      {showBorders && !isLastSlot && (
                        <div className={styles.slotDivider} />
                      )}
                    </>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default CardPreview;
